import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { useSelector } from "react-redux";
import { selectUser } from "../../../features/userSlice";
import {
  getWindowFormDefinition,
  getWindowFormDetail,
  getWindowFormMessages,
  postWindowFormMessage,
  updateWindowFormEntry,
  updateWindowFormStatus,
} from "../../../components/Forms/api";
import { normalizeDate, sendUserNotification } from "../../../utilities";
import FRM_SelectCustomer from "../../../components/Forms/Misc/FRM_SelectCustomer";
import FRM_QuoteOrder from "../../../components/Forms/Misc/FRM_QuoteOrder";
import CustomFormSections from "../../../components/Forms/CustomFormSections";
import {
  ChatComposer,
  MessageCard,
  StatusBadge,
  WorkflowStepper,
  parseRevisionMessage,
} from "./WindowFormShared";
import RequestQuoteOutlinedIcon from "@mui/icons-material/RequestQuoteOutlined";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import MarkEmailReadOutlinedIcon from "@mui/icons-material/MarkEmailReadOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import { PageHeader, PageShell } from "../../../components/UI/Page";
import { EmptyState, LoadingState } from "../../../components/UI/States";
import { BackLink, inputCls } from "../../../components/WindowDoor/wdUi";

// Salesperson view of a single RFQ: approve or decline the quote, revise the
// RFQ when the window team asks, and talk to the quoter in chat.
const WindowFormSalesDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const user = useSelector(selectUser);
  const [formData, setFormData] = useState(null);
  const [originalSnapshot, setOriginalSnapshot] = useState(null);
  const [sharedMessages, setSharedMessages] = useState([]);
  const [sharedText, setSharedText] = useState("");
  const [adminRevisionNotes, setAdminRevisionNotes] = useState("");
  const [revisionNotes, setRevisionNotes] = useState("");
  const [revisionSaving, setRevisionSaving] = useState(false);
  const [revisionOpen, setRevisionOpen] = useState(false);
  const [revisionFormData, setRevisionFormData] = useState(null);
  const [customFormDefinition, setCustomFormDefinition] = useState(null);
  const [sending, setSending] = useState(false);
  const [approvalSaving, setApprovalSaving] = useState(false);
  const [declineOpen, setDeclineOpen] = useState(false);
  const [closeReason, setCloseReason] = useState("");
  const sharedChatRef = useRef(null);

  useEffect(() => {
    getWindowFormDetail(setFormData, id);
    getWindowFormMessages(setSharedMessages, id, "shared");
  }, [id]);

  useEffect(() => {
    if (formData) {
      setAdminRevisionNotes(formData.status_notes || "");
      setRevisionNotes(formData.revision_notes || "");
      const snapshot = JSON.parse(JSON.stringify(formData));
      setOriginalSnapshot(snapshot);
      setRevisionFormData(snapshot);
    }
  }, [formData]);

  useEffect(() => {
    if (!formData || formData.form_id === 6 || formData.form_id === 11) {
      setCustomFormDefinition(null);
      return;
    }
    getWindowFormDefinition(setCustomFormDefinition, formData.form_id);
  }, [formData]);

  useEffect(() => {
    if (sharedChatRef.current) {
      sharedChatRef.current.scrollTop = sharedChatRef.current.scrollHeight;
    }
  }, [sharedMessages]);

  const status = formData?.status || "Submitted";
  const canSalesRevise = status !== "Converted to Order";

  const updateCustomFields = (updater) => {
    setRevisionFormData((prev) => {
      if (!prev) {
        return prev;
      }
      const currentFields = prev?.custom_fields || {};
      const nextFields =
        typeof updater === "function" ? updater(currentFields) : updater;
      return {
        ...prev,
        custom_fields: nextFields,
      };
    });
  };

  const handleFieldChange = (fieldId, value) => {
    updateCustomFields((current) => ({
      ...current,
      [fieldId]: value,
    }));
  };

  const handleSectionChange = (sectionId, rows) => {
    updateCustomFields((current) => ({
      ...current,
      [sectionId]: rows,
    }));
  };

  const renderRevisionForm = () => {
    if (!revisionFormData) {
      return null;
    }
    if (revisionFormData.form_id === 6 || revisionFormData.form_id === 11) {
      return null;
    }
    switch (revisionFormData.form_id) {
      default:
        return (
          <div className="space-y-4">
            <FRM_SelectCustomer
              formData={revisionFormData}
              setFormData={setRevisionFormData}
            />
            <FRM_QuoteOrder
              formData={revisionFormData}
              setFormData={setRevisionFormData}
            />
            <CustomFormSections
              sections={customFormDefinition?.sections || []}
              values={revisionFormData?.custom_fields || {}}
              onFieldChange={handleFieldChange}
              onSectionChange={handleSectionChange}
            />
          </div>
        );
    }
  };

  const formatRevisionChanges = (originalData, updatedData) => {
    if (!originalData || !updatedData) {
      return [];
    }
    const ignoredKeys = new Set([
      "_id",
      "entry_date",
      "last_save",
      "submit_user",
      "status",
      "status_notes",
      "revision_notes",
      "status_updated_at",
      "assigned_to",
      "assigned_date",
      "revised_at",
    ]);
    const buildCustomFieldMap = () => {
      const map = new Map();
      (customFormDefinition?.sections || []).forEach((section) => {
        (section.fields || []).forEach((field) => {
          if (field?.id) {
            map.set(field.id, {
              label: field.label || "Field",
              sectionTitle: section.title || "Section",
              isRepeatable: section.type === "repeatable",
            });
          }
        });
      });
      return map;
    };
    const formatValue = (value) => {
      if (value === null || value === undefined) {
        return "";
      }
      if (typeof value === "object") {
        return JSON.stringify(value);
      }
      return `${value}`;
    };
    const formatCustomFieldChanges = () => {
      const customChanges = [];
      const originalFields = originalData?.custom_fields || {};
      const updatedFields = updatedData?.custom_fields || {};
      const fieldMap = buildCustomFieldMap();
      const fieldIds = new Set([
        ...Object.keys(originalFields),
        ...Object.keys(updatedFields),
      ]);

      fieldIds.forEach((fieldId) => {
        const meta = fieldMap.get(fieldId);
        const originalValue = originalFields[fieldId];
        const updatedValue = updatedFields[fieldId];
        if (JSON.stringify(originalValue) === JSON.stringify(updatedValue)) {
          return;
        }
        if (Array.isArray(originalValue) || Array.isArray(updatedValue)) {
          const originalRows = Array.isArray(originalValue)
            ? originalValue
            : [];
          const updatedRows = Array.isArray(updatedValue) ? updatedValue : [];
          const maxRows = Math.max(originalRows.length, updatedRows.length);
          for (let rowIndex = 0; rowIndex < maxRows; rowIndex += 1) {
            const originalRow = originalRows[rowIndex] || {};
            const updatedRow = updatedRows[rowIndex] || {};
            const rowKeys = new Set([
              ...Object.keys(originalRow),
              ...Object.keys(updatedRow),
            ]);
            rowKeys.forEach((rowFieldId) => {
              const rowMeta = fieldMap.get(rowFieldId);
              const originalRowValue = originalRow[rowFieldId];
              const updatedRowValue = updatedRow[rowFieldId];
              if (
                JSON.stringify(originalRowValue) ===
                JSON.stringify(updatedRowValue)
              ) {
                return;
              }
              const labelBase = rowMeta?.label || rowFieldId;
              const sectionTitle =
                rowMeta?.sectionTitle || meta?.sectionTitle || "Section";
              customChanges.push({
                field: `${sectionTitle} - ${labelBase} (Row ${rowIndex + 1})`,
                from: formatValue(originalRowValue),
                to: formatValue(updatedRowValue),
              });
            });
          }
          return;
        }
        if (
          typeof originalValue === "object" ||
          typeof updatedValue === "object"
        ) {
          customChanges.push({
            field: meta?.label || fieldId,
            from: formatValue(originalValue),
            to: formatValue(updatedValue),
          });
          return;
        }
        if (meta) {
          customChanges.push({
            field: `${meta.sectionTitle} - ${meta.label}`,
            from: formatValue(originalValue),
            to: formatValue(updatedValue),
          });
          return;
        }
        customChanges.push({
          field: fieldId,
          from: formatValue(originalValue),
          to: formatValue(updatedValue),
        });
      });

      return customChanges;
    };
    const allKeys = new Set([
      ...Object.keys(originalData),
      ...Object.keys(updatedData),
    ]);
    const changes = [];
    const formatLabel = (value) =>
      value.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());

    allKeys.forEach((key) => {
      if (ignoredKeys.has(key)) {
        return;
      }
      if (key === "custom_fields") {
        changes.push(...formatCustomFieldChanges());
        return;
      }
      const originalValue = originalData[key];
      const updatedValue = updatedData[key];
      if (JSON.stringify(originalValue) === JSON.stringify(updatedValue)) {
        return;
      }
      if (
        Array.isArray(originalValue) ||
        Array.isArray(updatedValue) ||
        typeof originalValue === "object" ||
        typeof updatedValue === "object"
      ) {
        changes.push({
          field: formatLabel(key),
          from: "Updated",
          to: "Updated",
        });
        return;
      }
      changes.push({
        field: formatLabel(key),
        from: `${originalValue ?? ""}`,
        to: `${updatedValue ?? ""}`,
      });
    });

    return changes;
  };

  const buildRevisionMessage = ({
    revisionNumber,
    timestamp,
    changes,
    notes,
  }) => {
    return JSON.stringify({
      type: "revision",
      revisionNumber,
      timestamp,
      changes,
      notes,
    });
  };

  const latestQuoteMessageIndex = (sharedMessages || []).reduce(
    (latestIndex, message, index) =>
      message?.meta?.quote ? index : latestIndex,
    -1
  );
  const latestQuote =
    latestQuoteMessageIndex >= 0
      ? sharedMessages[latestQuoteMessageIndex]?.meta?.quote
      : null;

  const hasRevisionAfterLatestQuote =
    latestQuoteMessageIndex >= 0 &&
    (sharedMessages || [])
      .slice(latestQuoteMessageIndex + 1)
      .some((message) => Boolean(parseRevisionMessage(message)));

  const quoteNeedsDecision =
    status === "Awaiting Sales Approval" && !hasRevisionAfterLatestQuote;

  const handleSend = async () => {
    const trimmed = sharedText.trim();
    if (!trimmed) {
      return;
    }
    setSending(true);
    await postWindowFormMessage(
      id,
      {
        message: trimmed,
        user: {
          name: user?.name,
          userID: user?.userID,
          email: user?.email,
        },
      },
      "shared"
    );
    setSharedText("");
    await getWindowFormMessages(setSharedMessages, id, "shared");
    setSending(false);
  };

  // In-app notification to the quoter working this RFQ. Skip when the form
  // is unassigned or the quoter is the acting user.
  const notifyQuoter = (subject, message) => {
    const quoterEmail = formData?.assigned_to?.email;
    if (!quoterEmail || quoterEmail === user?.email) {
      return;
    }
    sendUserNotification(
      quoterEmail,
      user?.name || "Sales",
      message,
      subject,
      `/windowdoor/staff/windowforms/${id}`
    );
  };

  const handleRevisionSubmit = async () => {
    const updatedForm = revisionFormData || formData;
    if (!updatedForm || !canSalesRevise) {
      return;
    }
    const updatedPayload = {
      ...updatedForm,
      revision_notes: revisionNotes,
    };
    setRevisionSaving(true);
    await updateWindowFormEntry(id, updatedPayload);
    const timestamp = new Date().toLocaleString();
    const revisionChanges = formatRevisionChanges(
      originalSnapshot || formData,
      updatedPayload
    );
    const noteSuffix = revisionNotes.trim() ? revisionNotes : "";
    const revisionNumber =
      sharedMessages.filter((message) => parseRevisionMessage(message)).length +
      1;
    const messagePayload = buildRevisionMessage({
      revisionNumber,
      timestamp,
      changes: revisionChanges,
      notes: noteSuffix,
    });
    await postWindowFormMessage(
      id,
      {
        message: messagePayload,
        user: {
          name: user?.name,
          userID: user?.userID,
          email: user?.email,
        },
      },
      "shared"
    );
    await updateWindowFormStatus(id, {
      status: "Reviewing Revisions",
      status_notes: formData?.status_notes || "",
      order_number: formData?.order_number || "",
      cancelled_reason: formData?.cancelled_reason || "",
      updated_by: {
        name: user?.name,
        userID: user?.userID,
        email: user?.email,
      },
    });
    notifyQuoter(
      "Window RFQ revised",
      `${user?.name || "Sales"} submitted a revision on a window RFQ assigned to you.`
    );
    await getWindowFormDetail(setFormData, id);
    await getWindowFormMessages(setSharedMessages, id, "shared");
    setRevisionSaving(false);
    setRevisionOpen(false);
  };

  const handleApproveQuote = async () => {
    if (!formData?._id) {
      return;
    }
    setApprovalSaving(true);
    await updateWindowFormStatus(id, {
      status: "Approved",
      status_notes: formData?.status_notes || "",
      order_number: formData?.order_number || "",
      cancelled_reason: "",
      updated_by: {
        name: user?.name,
        userID: user?.userID,
        email: user?.email,
      },
    });
    notifyQuoter(
      "Window quote approved",
      `${user?.name || "Sales"} approved the quote — ready for the order number.`
    );
    await getWindowFormDetail(setFormData, id);
    await getWindowFormMessages(setSharedMessages, id, "shared");
    setApprovalSaving(false);
  };

  const handleCloseQuote = async () => {
    if (!formData?._id || !closeReason.trim()) {
      return;
    }
    setApprovalSaving(true);
    await updateWindowFormStatus(id, {
      status: "Cancelled",
      status_notes: formData?.status_notes || "",
      order_number: formData?.order_number || "",
      cancelled_reason: closeReason.trim(),
      updated_by: {
        name: user?.name,
        userID: user?.userID,
        email: user?.email,
      },
    });
    notifyQuoter(
      "Window RFQ declined",
      `${user?.name || "Sales"} declined and closed the RFQ: "${closeReason.trim()}"`
    );
    await getWindowFormDetail(setFormData, id);
    await getWindowFormMessages(setSharedMessages, id, "shared");
    setApprovalSaving(false);
    setCloseReason("");
    setDeclineOpen(false);
  };

  const secondaryButton = "app-btn app-btn-secondary";

  const detailRows = formData
    ? [
        ["Customer", formData.customer],
        ["Account", formData.account],
        ["Job", formData.job],
        ["Form", formData.form_name || "Window Form"],
        ["Form Type", formData.form_type?.toUpperCase()],
        ["Assigned Quoter", formData.assigned_to?.name || "Unassigned"],
        ["Order Number", formData.order_number],
      ].filter(([, value]) => value)
    : [];

  return (
    <PageShell size="2xl">
      <BackLink onClick={() => navigate("/windowdoor/sales/windowforms")}>
        Back to My Window Forms
      </BackLink>

      {!formData ? (
        <div className="app-card rounded-2xl border">
          <LoadingState label="Loading RFQ…" />
        </div>
      ) : (
        <>
          <PageHeader
            icon={RequestQuoteOutlinedIcon}
            tone="indigo"
            eyebrow="Window Quotes & Forms"
            title={formData.customer || "Window RFQ"}
            description={
              <>
                {[
                  formData.form_name || "Window Form",
                  formData.account,
                  formData.job,
                ]
                  .filter(Boolean)
                  .join(" • ")}
                {formData.entry_date && (
                  <span className="mt-0.5 block text-xs">
                    {`Submitted ${normalizeDate(new Date(formData.entry_date))}`}
                  </span>
                )}
              </>
            }
            actions={<StatusBadge status={status} />}
          />

          <div className="app-card mb-6 rounded-2xl border p-5">
            <WorkflowStepper
              status={status}
              audience="sales"
              detail={
                status === "Needs Revision"
                  ? formData.status_notes
                  : status === "Cancelled"
                  ? formData.cancelled_reason
                  : ""
              }
            />
          </div>

          {quoteNeedsDecision && (
            <section className="app-card mb-6 rounded-2xl border p-5 ring-2 ring-indigo-500/40">
              <div className="flex items-start gap-4">
                <span className="app-icon-tile hidden bg-indigo-500/15 text-indigo-600 dark:text-indigo-300 sm:inline-flex">
                  <MarkEmailReadOutlinedIcon />
                </span>
                <div className="min-w-0 flex-1">
                  <h2 className="text-lg font-semibold">
                    Quote ready for your review
                  </h2>
                  <p className="app-muted mt-0.5 text-sm">
                    The window team sent a quote. Review it, then approve it or
                    decline it. Need something changed instead? Use the revision
                    editor below.
                  </p>
                  {latestQuote && (
                    <a
                      className="app-surface mt-3 inline-flex max-w-full items-center gap-2 rounded-xl border px-3 py-2 text-sm font-medium transition hover:bg-[color:var(--app-hover)]"
                      href={latestQuote.url}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <PictureAsPdfOutlinedIcon
                        fontSize="small"
                        className="shrink-0 text-rose-600 dark:text-rose-400"
                      />
                      <span className="app-link break-all">
                        {latestQuote.originalName}
                      </span>
                    </a>
                  )}
                  <div className="mt-4 flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      className="app-btn bg-emerald-600 text-white hover:bg-emerald-700"
                      onClick={handleApproveQuote}
                      disabled={approvalSaving}
                    >
                      {approvalSaving ? "Saving..." : "Approve Quote"}
                    </button>
                    <button
                      type="button"
                      className={secondaryButton}
                      onClick={() => setDeclineOpen((prev) => !prev)}
                      disabled={approvalSaving}
                    >
                      Decline Quote
                    </button>
                  </div>
                  {declineOpen && (
                    <div className="mt-4 space-y-3 rounded-xl border border-rose-500/25 bg-rose-500/10 p-4">
                      <p className="text-sm font-semibold">
                        Decline and close this RFQ
                      </p>
                      <textarea
                        className={inputCls}
                        rows={2}
                        aria-label="Reason for declining"
                        placeholder="Reason for declining..."
                        value={closeReason}
                        onChange={(event) => setCloseReason(event.target.value)}
                        disabled={approvalSaving}
                      />
                      <div className="flex justify-end">
                        <button
                          type="button"
                          className="app-btn app-btn-danger"
                          onClick={handleCloseQuote}
                          disabled={approvalSaving || !closeReason.trim()}
                        >
                          {approvalSaving ? "Saving..." : "Decline & Close"}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </section>
          )}

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <div className="min-w-0 space-y-6 lg:col-span-2">
              {canSalesRevise && (
                <section className="app-card rounded-2xl border p-5">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="min-w-0">
                      <h2 className="text-lg font-semibold">Revise the RFQ</h2>
                      <p className="app-muted mt-0.5 text-sm">
                        Update the details and submit — every revision is
                        logged in chat so the window team can see what
                        changed.
                      </p>
                    </div>
                    <button
                      type="button"
                      className={secondaryButton}
                      onClick={() => setRevisionOpen((prev) => !prev)}
                    >
                      {revisionOpen ? "Close Editor" : "Edit RFQ"}
                    </button>
                  </div>
                  {adminRevisionNotes && (
                    <div className="mt-4 rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm">
                      <p className="mb-1 font-semibold">
                        What the window team asked for
                      </p>
                      <p className="whitespace-pre-wrap">
                        {adminRevisionNotes}
                      </p>
                    </div>
                  )}
                  {revisionOpen && (
                    <>
                      <div className="app-surface mt-4 rounded-xl border p-4">
                        {renderRevisionForm()}
                      </div>
                      <label
                        htmlFor="rfq-revision-notes"
                        className="mb-1.5 mt-4 block text-sm font-semibold"
                      >
                        What did you change?
                      </label>
                      <textarea
                        id="rfq-revision-notes"
                        className={inputCls}
                        rows={3}
                        value={revisionNotes}
                        onChange={(event) =>
                          setRevisionNotes(event.target.value)
                        }
                        placeholder="Describe the changes you made..."
                      />
                      <div className="mt-4 flex justify-end">
                        <button
                          type="button"
                          className="app-btn app-btn-primary"
                          onClick={handleRevisionSubmit}
                          disabled={revisionSaving}
                        >
                          {revisionSaving
                            ? "Saving Revision..."
                            : "Submit Revision"}
                        </button>
                      </div>
                    </>
                  )}
                </section>
              )}

              <section className="app-card rounded-2xl border p-5">
                <h2 className="mb-4 text-lg font-semibold">RFQ Details</h2>
                <dl className="grid grid-cols-1 gap-x-6 gap-y-4 text-sm sm:grid-cols-2">
                  {detailRows.map(([label, value]) => (
                    <div key={label} className="min-w-0">
                      <dt className="app-muted text-xs font-medium">{label}</dt>
                      <dd className="mt-0.5 break-words font-semibold">
                        {value}
                      </dd>
                    </div>
                  ))}
                </dl>
              </section>

              <section className="app-card rounded-2xl border p-5">
                <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                  <h2 className="text-lg font-semibold">RFQ Document</h2>
                  <a
                    className="app-btn app-btn-secondary app-btn-sm"
                    href={`/api/forms/pdf/?id=${formData._id}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <OpenInNewIcon sx={{ fontSize: 16 }} />
                    Open in new tab
                  </a>
                </div>
                <iframe
                  title="Window RFQ PDF"
                  src={`/api/forms/pdf/?id=${formData._id}`}
                  className="h-[560px] w-full rounded-xl border app-divider bg-white"
                />
              </section>
            </div>

            <aside className="app-card h-fit rounded-2xl border p-4">
              <h2 className="px-1 font-semibold">Chat with the Window Team</h2>
              <p className="app-muted mb-3 px-1 text-xs">
                Quotes, revisions, and status changes show up here too.
              </p>
              <div
                ref={sharedChatRef}
                className="max-h-[480px] space-y-3 overflow-y-auto rounded-xl border app-divider p-3"
              >
                {sharedMessages.length === 0 && (
                  <EmptyState
                    compact
                    icon={ChatBubbleOutlineIcon}
                    description="No messages yet. Start the conversation."
                  />
                )}
                {sharedMessages.map((message, index) => (
                  <MessageCard
                    key={`${message.createdAt}-${index}`}
                    message={message}
                    isOwn={message?.user?.userID === user?.userID}
                  />
                ))}
              </div>
              <ChatComposer
                value={sharedText}
                onChange={setSharedText}
                onSend={handleSend}
                disabled={sending}
                placeholder="Message the window team..."
                buttonLabel={sending ? "Sending..." : "Send"}
              />
            </aside>
          </div>
        </>
      )}
    </PageShell>
  );
};

export default WindowFormSalesDetail;
